import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import apiService from "../services/api";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications deve ser usado dentro de NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isConnected, onNewNotification } = useSocket();
  const [toastNotifications, setToastNotifications] = useState([]);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  const isFirstLoad = useRef(true);

  // Listener para notificações via Socket.IO (tempo real)
  useEffect(() => {
    // Aguarda autenticação terminar antes de verificar notificações
    if (authLoading || !user || !isConnected) return;

    const unsubscribe = onNewNotification((notificationData, timestamp) => {
      // Atualizar último ID
      if (notificationData.id !== lastNotificationId) {
        setLastNotificationId(notificationData.id);
        addToastNotification(notificationData);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [user, isConnected, lastNotificationId, onNewNotification, authLoading]);

  // Fallback: Polling a cada 60 segundos (apenas se Socket.IO não estiver conectado)
  useEffect(() => {
    // Aguarda autenticação terminar antes de verificar notificações
    if (authLoading) return;

    if (!user) {
      setToastNotifications([]);
      setLastNotificationId(null);
      isFirstLoad.current = true;
      return;
    }

    const checkForNewNotifications = async () => {
      try {
        const response = await apiService.getNotifications(1, 1, true);
        const notifications = response.requests || [];

        if (notifications.length > 0) {
          const latestNotification = notifications[0];

          if (latestNotification.id !== lastNotificationId) {
            setLastNotificationId(latestNotification.id);

            // Adicionar toast apenas se não for a primeira verificação
            // E apenas se Socket.IO não estiver conectado (para evitar duplicatas)
            if (!isFirstLoad.current && !isConnected) {
              addToastNotification(latestNotification);
            }
          }
        }
        isFirstLoad.current = false;
      } catch (error) {
        // Silencia erros de autenticação (401/403) quando usuário não está logado
        if (!error.message?.includes('401') && !error.message?.includes('403')) {
          console.error('Erro ao verificar notificações:', error);
        }
      }
    };

    // Verificação inicial
    checkForNewNotifications();

    // Polling a cada 60 segundos como fallback (antes era 15s)
    const interval = setInterval(checkForNewNotifications, 60000);

    return () => clearInterval(interval);
  }, [user, authLoading, lastNotificationId, isConnected]);

  const addToastNotification = (notification) => {
    const toastId = `toast-${notification.id}-${Date.now()}`;
    const toastNotification = {
      ...notification,
      toastId,
    };

    setToastNotifications((prev) => [...prev, toastNotification]);
  };

  const removeToastNotification = (toastId) => {
    setToastNotifications((prev) =>
      prev.filter((notification) => notification.toastId !== toastId)
    );
  };

  const acknowledgeNotification = (notificationId) => {
    setToastNotifications((prev) =>
      prev.filter((notification) => notification.id !== notificationId)
    );
  };

  const value = {
    toastNotifications,
    addToastNotification,
    removeToastNotification,
    acknowledgeNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;

